<script setup lang="ts">
import { Order } from '@/@types/order'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useForm } from '@inertiajs/vue3'
import { Loader2, Trash } from 'lucide-vue-next'
import { ref } from 'vue'

const { order } = defineProps<{ order: Order }>()

const form = useForm({})

const typeToExclude = ref<string>('')
</script>

<template>
    <Dialog>
        <DialogOverlay class="z-60 bg-black/10" />
        <DialogTrigger class="w-full cursor-pointer">
            <DropdownMenuItem @select.prevent class="w-full">
                <Button variant="ghost" size="sm" class="text-muted-foreground w-full justify-start"><Trash /> {{ $t('Delete order') }} </Button>
            </DropdownMenuItem>
        </DialogTrigger>
        <DialogContent class="font-inter p-8 text-gray-800">
            <DialogHeader class="flex gap-2">
                <DialogTitle class="text-2xl">{{ $t('Are you sure you want to delete order', { order_id: order.id }) }}</DialogTitle>
                <DialogDescription class="text-sm font-normal text-gray-800"
                    >{{ $t('This action is irreversible and the order will be permanently deleted. Do you want to proceed?') }}
                </DialogDescription>
            </DialogHeader>

            <div>
                <Label required for="type">{{ $t("Please type 'DELETE' to confirm.") }}</Label>
                <Input
                    v-model="typeToExclude"
                    type="text"
                    id="type"
                    @input="(e: Event) => (typeToExclude = (e.target as HTMLInputElement).value.toUpperCase())"
                />
            </div>

            <DialogFooter class="pt-2">
                <form @submit.prevent="form.delete(`/admin/orders/${order.id}`, { preserveState: false })" class="flex w-full flex-col gap-6">
                    <div class="flex justify-between">
                        <DialogClose>
                            <Button type="button" variant="ghost" class="text-primary hover:text-primary hover:bg-primary/3 min-w-2/12">
                                {{ $t('Back') }}
                            </Button>
                        </DialogClose>
                        <Button :disabled="form.processing || typeToExclude !== 'DELETAR'" class="min-w-2/12">
                            <template v-if="!form.processing">
                                {{ $t('Delete order') }}
                            </template>
                            <template v-if="form.processing">
                                <span>
                                    <Loader2 class="animate-spin" />
                                    <span class="sr-only"></span>
                                </span>
                            </template>
                        </Button>
                    </div>
                </form>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
